import React, { useRef, createElement } from 'react';
/**
 * DjayAvatar — Inline SVG mascotte with 9 emotions, 8 variants, 3 tiers.
 * DDD co-locatie: moved from components/mascot/ in s60.
 *
 * Import note (audit-referentie: Ronde 10): only `useRef` is imported.
 * `createElement` is NOT needed — JSX is transformed by the bundler. Adding
 * unused React imports has regressed twice (ronde 6 DEAD-4, ronde 8); the
 * `unused-imports/no-unused-imports` rule in `.eslintrc.json` is meant to
 * catch this once lint is actually wired into CI.
 */
export type DjaySize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'hero';
export type DjayEmotion = 'happy' | 'excited' | 'encouraging' | 'celebrating' | 'thinking' | 'sleeping' | 'pointing' | 'surprised' | 'proud';
export type DjayTier = 'rookie' | 'pro' | 'master';
export type DjayVariant = 'default' | 'blaze' | 'frost' | 'pixel' | 'royal' | 'cosmic' | 'shadow' | 'goud' | 'rainbow';
export type DjayAccessory = 'none' | 'seedling' | 'pencil' | 'badge100' | 'smallFlame' | 'trophy' | 'doubleFlameCrown' | 'starWand' | 'tripleStars' | 'scepter' | 'leafGarland' | 'medal' | 'cosmicOrb' | 'shadowOrb' | 'sunglasses' | 'cap' | 'goldChain';
export interface DjayAvatarProps {
  size?: DjaySize;
  emotion?: DjayEmotion;
  tier?: DjayTier;
  variant?: DjayVariant;
  accessory?: DjayAccessory;
  className?: string;
  animate?: boolean;
  hideBody?: boolean;
}
const SIZES: Record<DjaySize, number> = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64,
  hero: 128
};
interface VariantColorScheme {
  bodyGradient: [string, string, string];
  flameColors: [string, string, string, string];
  glassesFrame: string;
  glassesLens: string;
  armColor: string;
  armHighlight: string;
  tailStroke: string;
  tailHighlight: string;
  feetDark: string;
  feetLight: string;
  mouthColor: string;
}
export const VARIANT_COLORS: Record<DjayVariant, VariantColorScheme> = {
  default: {
    bodyGradient: ['#FFB057', '#FF8A00', '#E67A00'],
    flameColors: ['#FFD700', '#FF8A00', '#E67A00', '#CC5500'],
    glassesFrame: '#1E293B',
    glassesLens: '#E0F2FE',
    armColor: '#E67A00',
    armHighlight: '#FFB057',
    tailStroke: '#E67A00',
    tailHighlight: '#FFB057',
    feetDark: '#E67A00',
    feetLight: '#FFB057',
    mouthColor: '#1E293B'
  },
  blaze: {
    bodyGradient: ['#F87171', '#EF4444', '#DC2626'],
    flameColors: ['#FCA5A5', '#EF4444', '#DC2626', '#991B1B'],
    glassesFrame: '#1E293B',
    glassesLens: '#FEE2E2',
    armColor: '#DC2626',
    armHighlight: '#F87171',
    tailStroke: '#DC2626',
    tailHighlight: '#F87171',
    feetDark: '#DC2626',
    feetLight: '#F87171',
    mouthColor: '#1E293B'
  },
  frost: {
    bodyGradient: ['#60A5FA', '#3B82F6', '#2563EB'],
    flameColors: ['#93C5FD', '#3B82F6', '#2563EB', '#1D4ED8'],
    glassesFrame: '#1E293B',
    glassesLens: '#DBEAFE',
    armColor: '#2563EB',
    armHighlight: '#60A5FA',
    tailStroke: '#2563EB',
    tailHighlight: '#60A5FA',
    feetDark: '#2563EB',
    feetLight: '#60A5FA',
    mouthColor: '#1E293B'
  },
  pixel: {
    bodyGradient: ['#34D399', '#10B981', '#059669'],
    flameColors: ['#6EE7B7', '#10B981', '#059669', '#047857'],
    glassesFrame: '#1E293B',
    glassesLens: '#D1FAE5',
    armColor: '#059669',
    armHighlight: '#34D399',
    tailStroke: '#059669',
    tailHighlight: '#34D399',
    feetDark: '#059669',
    feetLight: '#34D399',
    mouthColor: '#1E293B'
  },
  royal: {
    bodyGradient: ['#A78BFA', '#8B5CF6', '#7C3AED'],
    flameColors: ['#C4B5FD', '#8B5CF6', '#7C3AED', '#6D28D9'],
    glassesFrame: '#1E293B',
    glassesLens: '#EDE9FE',
    armColor: '#7C3AED',
    armHighlight: '#A78BFA',
    tailStroke: '#7C3AED',
    tailHighlight: '#A78BFA',
    feetDark: '#7C3AED',
    feetLight: '#A78BFA',
    mouthColor: '#1E293B'
  },
  cosmic: {
    bodyGradient: ['#818CF8', '#4F46E5', '#4338CA'],
    flameColors: ['#A5B4FC', '#4F46E5', '#4338CA', '#3730A3'],
    glassesFrame: '#1E293B',
    glassesLens: '#E0E7FF',
    armColor: '#4338CA',
    armHighlight: '#818CF8',
    tailStroke: '#4338CA',
    tailHighlight: '#818CF8',
    feetDark: '#4338CA',
    feetLight: '#818CF8',
    mouthColor: '#1E293B'
  },
  shadow: {
    bodyGradient: ['#64748B', '#334155', '#1E293B'],
    flameColors: ['#94A3B8', '#334155', '#1E293B', '#0F172A'],
    glassesFrame: '#0F172A',
    glassesLens: '#CBD5E1',
    armColor: '#1E293B',
    armHighlight: '#64748B',
    tailStroke: '#1E293B',
    tailHighlight: '#64748B',
    feetDark: '#1E293B',
    feetLight: '#64748B',
    mouthColor: '#0F172A'
  },
  goud: {
    bodyGradient: ['#FCD34D', '#F59E0B', '#D97706'],
    flameColors: ['#FDE068', '#F59E0B', '#D97706', '#B45309'],
    glassesFrame: '#92400E',
    glassesLens: '#FEF3C7',
    armColor: '#D97706',
    armHighlight: '#FCD34D',
    tailStroke: '#D97706',
    tailHighlight: '#FCD34D',
    feetDark: '#D97706',
    feetLight: '#FCD34D',
    mouthColor: '#92400E'
  },
  rainbow: {
    // Fallback 3-stop scheme (rainbow body uses a 6-stop gradient below, but
    // this scheme still drives non-body parts: arms, tail, feet, flame, mouth).
    bodyGradient: ['#F87171', '#FBBF24', '#60A5FA'],
    flameColors: ['#FDE047', '#F472B6', '#A78BFA', '#7C3AED'],
    glassesFrame: '#1E293B',
    glassesLens: '#FEF3C7',
    armColor: '#8B5CF6',
    armHighlight: '#F472B6',
    tailStroke: '#3B82F6',
    tailHighlight: '#FCD34D',
    feetDark: '#8B5CF6',
    feetLight: '#F472B6',
    mouthColor: '#1E293B'
  }
};
// ── Singleton animation styles ───────────────────────────────────────
// Injected once into <head> to avoid duplicate <style> tags when
// multiple animated DjayAvatars are rendered simultaneously.
let _stylesInjected = false;
function ensureAnimationStyles() {
  if (_stylesInjected || typeof document === 'undefined') return;
  _stylesInjected = true;
  const style = document.createElement('style');
  style.setAttribute('data-djay-avatar', 'animations');
  style.textContent = `
    .djay-animated .djay-eyes { transform-origin: center; animation: djay-blink 4s ease-in-out infinite; }
    @keyframes djay-blink { 0%, 90%, 100% { transform: scaleY(1); } 95% { transform: scaleY(0.1); } }
    .djay-animated .djay-body { transform-origin: center bottom; animation: djay-wobble 6s ease-in-out infinite; }
    @keyframes djay-wobble { 0%, 100% { transform: rotate(0deg); } 25% { transform: rotate(-1.5deg); } 75% { transform: rotate(1.5deg); } }
    .djay-animated .djay-flame { transform-origin: center bottom; animation: djay-flame-flicker 3s ease-in-out infinite; }
    @keyframes djay-flame-flicker { 0%, 100% { transform: scaleY(1) scaleX(1); } 33% { transform: scaleY(1.08) scaleX(0.95); } 66% { transform: scaleY(0.95) scaleX(1.05); } }
    .djay-animated .djay-tail { transform-origin: left center; animation: djay-tail-wag 4s ease-in-out infinite; }
    @keyframes djay-tail-wag { 0%, 100% { transform: rotate(0deg); } 25% { transform: rotate(8deg); } 75% { transform: rotate(-5deg); } }
    .djay-animated .djay-glasses { transform-origin: center; animation: djay-glasses-settle 8s ease-in-out infinite; }
    @keyframes djay-glasses-settle { 0%, 90%, 100% { transform: translateY(0); } 93% { transform: translateY(-1.5px); } 96% { transform: translateY(0.5px); } }
    .djay-animated .djay-shadow { transform-origin: center; animation: djay-shadow-pulse 6s ease-in-out infinite; }
    @keyframes djay-shadow-pulse { 0%, 100% { transform: scaleX(1); opacity: 1; } 25% { transform: scaleX(1.03); opacity: 0.9; } 75% { transform: scaleX(0.97); opacity: 0.9; } }
    .djay-animated .djay-arm-right { animation: djay-point-bob 2s ease-in-out infinite; }
    @keyframes djay-point-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }
    .djay-spin { transform-origin: center; animation: djay-spin-anim 4s linear infinite; }
    @keyframes djay-spin-anim { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .djay-spin-reverse { transform-origin: center; animation: djay-spin-reverse-anim 5s linear infinite; }
    @keyframes djay-spin-reverse-anim { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
    .djay-animated .djay-accessory-float { transform-origin: center; animation: djay-accessory-float 3s ease-in-out infinite; }
    @keyframes djay-accessory-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
    @media (prefers-reduced-motion: reduce) { .djay-animated .djay-eyes, .djay-animated .djay-body, .djay-animated .djay-flame, .djay-animated .djay-tail, .djay-animated .djay-arm-left, .djay-animated .djay-arm-right, .djay-animated .djay-glasses, .djay-animated .djay-shadow, .djay-spin, .djay-spin-reverse, .djay-accessory-float { animation: none !important; } }
  `;
  document.head.appendChild(style);
}
export function DjayAvatar({
  size = 'md',
  emotion = 'happy',
  tier = 'rookie',
  variant = 'default',
  accessory = 'none',
  className = '',
  animate = true,
  hideBody = false
}: DjayAvatarProps) {
  const pixelSize = SIZES[size];
  const showDetails = pixelSize >= 32;
  const showArmStubs = pixelSize >= 24 && pixelSize < 32;
  const showFullArms = pixelSize >= 32;
  const showTail = pixelSize >= 32;
  const showMouth = pixelSize >= 24;
  // If hideBody is true, we force showAccessory to true regardless of size so we can render just the accessory
  const showAccessory = (pixelSize >= 32 || hideBody) && accessory !== 'none';
  const uidRef = useRef(`djay-${size}-${variant}-${Math.random().toString(36).slice(2, 6)}`);
  const uid = uidRef.current;
  const colors = VARIANT_COLORS[variant] ?? VARIANT_COLORS.default;
  const renderEyes = () => {
    switch (emotion) {
      case 'sleeping':
        return <g className="djay-eyes" data-id="element-2986">
            <path d="M60 102 Q70 108 80 102" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-2987" />
            <path d="M120 102 Q130 108 140 102" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-2988" />
          </g>;
      case 'celebrating':
        return <g className="djay-eyes" data-id="element-2989">
            <path d="M60 105 Q70 95 80 105" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-2990" />
            <path d="M120 105 Q130 95 140 105" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-2991" />
          </g>;
      case 'encouraging':
        return <g className="djay-eyes" data-id="element-2992">
            <path d="M60 100 Q70 95 80 100" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-2993" />
            <path d="M120 100 Q130 95 140 100" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-2994" />
          </g>;
      case 'proud':
        return <g className="djay-eyes" data-id="element-2995">
            <path d="M60 100 Q70 90 80 100" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-2996" />
            <path d="M70 92 L72 96 L76 96 L73 99 L74 103 L70 100 L66 103 L67 99 L64 96 L68 96 Z" fill="#FBBF24" data-id="element-2997" />
            <path d="M120 100 Q130 90 140 100" stroke="#1E293B" strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-2998" />
            <path d="M130 92 L132 96 L136 96 L133 99 L134 103 L130 100 L126 103 L127 99 L124 96 L128 96 Z" fill="#FBBF24" data-id="element-2999" />
          </g>;
      default:
        {
          let pupilXOffset = 0,
            pupilYOffset = 0,
            pupilSize = 3.5,
            showSparkles = true;
          if (emotion === 'thinking') {
            pupilXOffset = 3;
            pupilYOffset = -3;
          } else if (emotion === 'pointing') {
            pupilXOffset = 5;
          } else if (emotion === 'surprised') {
            pupilSize = 2;
            showSparkles = false;
          }
          return <g className="djay-eyes" data-id="element-3000">
            <ellipse cx="70" cy="100" rx="10" ry="12" fill="white" data-id="element-3001" />
            <ellipse cx={72 + pupilXOffset} cy={102 + pupilYOffset} rx="6" ry="7" fill="#475569" data-id="element-3002" />
            <circle cx={73 + pupilXOffset} cy={103 + pupilYOffset} r={pupilSize} fill="#0F172A" data-id="element-3003" />
            {showSparkles && <circle cx={76 + pupilXOffset} cy={99 + pupilYOffset} r={emotion === 'excited' ? 3.5 : 2.5} fill="white" data-id="element-3004" />}
            {showSparkles && <circle cx={69 + pupilXOffset} cy={105 + pupilYOffset} r="1" fill="white" fillOpacity="0.7" data-id="element-3005" />}
            <ellipse cx="130" cy="100" rx="10" ry="12" fill="white" data-id="element-3006" />
            <ellipse cx={132 + pupilXOffset} cy={102 + pupilYOffset} rx="6" ry="7" fill="#475569" data-id="element-3007" />
            <circle cx={133 + pupilXOffset} cy={103 + pupilYOffset} r={pupilSize} fill="#0F172A" data-id="element-3008" />
            {showSparkles && <circle cx={136 + pupilXOffset} cy={99 + pupilYOffset} r={emotion === 'excited' ? 3.5 : 2.5} fill="white" data-id="element-3009" />}
            {showSparkles && <circle cx={129 + pupilXOffset} cy={105 + pupilYOffset} r="1" fill="white" fillOpacity="0.7" data-id="element-3010" />}
          </g>;
        }
    }
  };
  const renderMouth = () => {
    if (!showMouth) return null;
    switch (emotion) {
      case 'excited':
      case 'celebrating':
      case 'proud':
        return <g className="djay-mouth" data-id="element-3011">
            <path d="M80 130 Q100 165 120 130 Z" fill={colors.mouthColor} data-id="element-3012" />
            <path d="M90 145 Q100 155 110 145 Z" fill="#EF4444" data-id="element-3013" />
          </g>;
      case 'surprised':
        return <g className="djay-mouth" data-id="element-3014">
            <ellipse cx="100" cy="138" rx="6" ry="10" fill={colors.mouthColor} data-id="element-3015" />
          </g>;
      case 'thinking':
        return <g className="djay-mouth" data-id="element-3016">
            <path d="M90 135 Q95 132 100 135 T110 135" stroke={colors.mouthColor} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3017" />
          </g>;
      case 'sleeping':
        return <g className="djay-mouth" data-id="element-3018">
            <path d="M95 138 Q100 142 105 138" stroke={colors.mouthColor} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3019" />
          </g>;
      case 'pointing':
        return <g className="djay-mouth" data-id="element-3020">
            <path d="M90 135 Q105 142 115 130" stroke={colors.mouthColor} strokeWidth="3.5" strokeLinecap="round" fill="none" data-id="element-3021" />
          </g>;
      case 'encouraging':
        return <g className="djay-mouth" data-id="element-3022">
            <path d="M85 135 Q100 145 115 135" stroke={colors.mouthColor} strokeWidth="3.5" strokeLinecap="round" fill="none" data-id="element-3023" />
          </g>;
      case 'happy':
      default:
        return <g className="djay-mouth" data-id="element-3024">
            <path d="M82 133 Q100 152 118 133" stroke={colors.mouthColor} strokeWidth="3.5" strokeLinecap="round" fill="none" data-id="element-3025" />
            {showDetails && <path d="M85 134 Q100 148 115 134" fill={colors.mouthColor} fillOpacity="0.15" data-id="element-3026" />}
          </g>;
    }
  };
  const renderArms = () => {
    if (!showFullArms && !showArmStubs) return null;
    if (showArmStubs) return <g className="djay-arms-stubs" data-id="element-3027">
          <circle cx="38" cy="125" r="8" fill={colors.armColor} data-id="element-3028" />
          <circle cx="162" cy="125" r="8" fill={colors.armColor} data-id="element-3029" />
        </g>;
    switch (emotion) {
      case 'pointing':
        return <>
            <g className="djay-arm-left" data-id="element-3030">
              <ellipse cx="42" cy="135" rx="14" ry="18" fill={colors.armColor} transform="rotate(15 42 135)" data-id="element-3031" />
            </g>
            <g className="djay-arm-right" style={{
            transformOrigin: '150px 120px'
          }} data-id="element-3032">
              <ellipse cx="165" cy="120" rx="22" ry="12" fill={colors.armColor} transform="rotate(-10 165 120)" data-id="element-3033" />
              <path d="M175 118 L192 115" stroke={colors.armColor} strokeWidth="10" strokeLinecap="round" data-id="element-3034" />
              <path d="M175 118 L192 115" stroke={colors.armHighlight} strokeWidth="4" strokeLinecap="round" data-id="element-3035" />
              <ellipse cx="170" cy="118" rx="8" ry="6" fill={colors.armHighlight} transform="rotate(-10 170 118)" data-id="element-3036" />
            </g>
          </>;
      case 'celebrating':
      case 'excited':
      case 'proud':
        return <>
            <g className="djay-arm-left" data-id="element-3037">
              <ellipse cx="40" cy="90" rx="14" ry="25" fill={colors.armColor} transform="rotate(35 40 90)" data-id="element-3038" />
              <ellipse cx="32" cy="75" rx="8" ry="10" fill={colors.armHighlight} transform="rotate(35 32 75)" data-id="element-3039" />
            </g>
            <g className="djay-arm-right" data-id="element-3040">
              <ellipse cx="160" cy="90" rx="14" ry="25" fill={colors.armColor} transform="rotate(-35 160 90)" data-id="element-3041" />
              <ellipse cx="168" cy="75" rx="8" ry="10" fill={colors.armHighlight} transform="rotate(-35 168 75)" data-id="element-3042" />
            </g>
          </>;
      case 'thinking':
        return <>
            <g className="djay-arm-left" data-id="element-3043">
              <ellipse cx="42" cy="135" rx="14" ry="18" fill={colors.armColor} transform="rotate(15 42 135)" data-id="element-3044" />
            </g>
            <g className="djay-arm-right" data-id="element-3045">
              <ellipse cx="135" cy="135" rx="14" ry="22" fill={colors.armColor} transform="rotate(-45 135 135)" data-id="element-3046" />
              <ellipse cx="125" cy="120" rx="8" ry="10" fill={colors.armHighlight} transform="rotate(-45 125 120)" data-id="element-3047" />
            </g>
          </>;
      case 'surprised':
        return <>
            <g className="djay-arm-left" data-id="element-3048">
              <ellipse cx="30" cy="125" rx="18" ry="14" fill={colors.armColor} transform="rotate(-20 30 125)" data-id="element-3049" />
            </g>
            <g className="djay-arm-right" data-id="element-3050">
              <ellipse cx="170" cy="125" rx="18" ry="14" fill={colors.armColor} transform="rotate(20 170 125)" data-id="element-3051" />
            </g>
          </>;
      case 'sleeping':
        return <>
            <g className="djay-arm-left" data-id="element-3052">
              <ellipse cx="55" cy="145" rx="16" ry="12" fill={colors.armColor} transform="rotate(20 55 145)" data-id="element-3053" />
            </g>
            <g className="djay-arm-right" data-id="element-3054">
              <ellipse cx="145" cy="145" rx="16" ry="12" fill={colors.armColor} transform="rotate(-20 145 145)" data-id="element-3055" />
            </g>
          </>;
      case 'encouraging':
        return <>
            <g className="djay-arm-left" data-id="element-3056">
              <ellipse cx="42" cy="125" rx="18" ry="22" fill={colors.armColor} transform="rotate(-20 42 125)" data-id="element-3057" />
            </g>
            <g className="djay-arm-right" data-id="element-3058">
              <ellipse cx="165" cy="135" rx="20" ry="14" fill={colors.armColor} transform="rotate(-15 165 135)" data-id="element-3059" />
              <ellipse cx="175" cy="130" rx="8" ry="10" fill={colors.armHighlight} transform="rotate(-15 175 130)" data-id="element-3060" />
            </g>
          </>;
      case 'happy':
      default:
        return <>
            <g className="djay-arm-left" data-id="element-3061">
              <ellipse cx="42" cy="125" rx="18" ry="22" fill={colors.armColor} transform="rotate(-20 42 125)" data-id="element-3062" />
              <ellipse cx="38" cy="118" rx="8" ry="10" fill={colors.armHighlight} transform="rotate(-20 38 118)" data-id="element-3063" />
            </g>
            <g className="djay-arm-right" data-id="element-3064">
              <ellipse cx="158" cy="130" rx="18" ry="22" fill={colors.armColor} transform="rotate(15 158 130)" data-id="element-3065" />
              <ellipse cx="154" cy="123" rx="8" ry="10" fill={colors.armHighlight} transform="rotate(15 154 123)" data-id="element-3066" />
            </g>
          </>;
    }
  };
  const renderAccessory = () => {
    if (!showAccessory) return null;
    switch (accessory) {
      // HAND ACCESSORIES
      case 'seedling':
        return <g className="djay-accessory" transform="translate(165, 110) scale(1.5)" data-id="element-3067">
            <path d="M0 20 Q-5 10 -15 5 Q-5 15 0 20 Z" fill="#4ADE80" data-id="element-3068" />
            <path d="M0 20 Q5 10 15 5 Q5 15 0 20 Z" fill="#22C55E" data-id="element-3069" />
            <path d="M0 20 L0 30" stroke="#166534" strokeWidth="3" strokeLinecap="round" data-id="element-3070" />
          </g>;
      case 'pencil':
        return <g className="djay-accessory" transform="translate(165, 100) rotate(45) scale(1.5)" data-id="element-3071">
            <rect x="-5" y="0" width="10" height="30" fill="#FBBF24" rx="2" data-id="element-3072" />
            <path d="M-5 0 L0 -10 L5 0 Z" fill="#FCA5A5" data-id="element-3073" />
            <path d="M-2 -6 L0 -10 L2 -6 Z" fill="#1E293B" data-id="element-3074" />
            <rect x="-5" y="30" width="10" height="5" fill="#94A3B8" data-id="element-3075" />
            <rect x="-5" y="35" width="10" height="8" fill="#F87171" rx="2" data-id="element-3076" />
          </g>;
      case 'smallFlame':
        return <g className="djay-accessory" transform="translate(165, 110) scale(1.5)" data-id="element-3077">
            <path d="M0 20 C-10 20 -15 10 -5 0 C0 -10 5 -20 0 -25 C10 -15 15 -5 5 5 C10 10 10 20 0 20 Z" fill="#F97316" data-id="element-3078" />
            <path d="M0 15 C-5 15 -8 8 -2 0 C0 -5 2 -10 0 -12 C5 -7 8 -2 2 3 C5 5 5 15 0 15 Z" fill="#FBBF24" data-id="element-3079" />
          </g>;
      case 'trophy':
        return <g className="djay-accessory" transform="translate(165, 100) scale(1.5)" data-id="element-3080">
            <path d="M-15 0 L15 0 L10 20 L-10 20 Z" fill="#FBBF24" data-id="element-3081" />
            <path d="M-5 20 L5 20 L5 30 L-5 30 Z" fill="#D97706" data-id="element-3082" />
            <path d="M-12 30 L12 30 L12 35 L-12 35 Z" fill="#92400E" data-id="element-3083" />
            <path d="M-15 5 C-25 5 -25 15 -10 15" fill="none" stroke="#FBBF24" strokeWidth="3" strokeLinecap="round" data-id="element-3084" />
            <path d="M15 5 C25 5 25 15 10 15" fill="none" stroke="#FBBF24" strokeWidth="3" strokeLinecap="round" data-id="element-3085" />
          </g>;
      case 'starWand':
        return <g className="djay-accessory" transform="translate(165, 100) rotate(15) scale(1.5)" data-id="element-3086">
            <rect x="-2" y="0" width="4" height="40" fill="#D97706" rx="2" data-id="element-3087" />
            <path d="M0 -15 L4 -4 L15 -4 L6 3 L10 14 L0 7 L-10 14 L-6 3 L-15 -4 L-4 -4 Z" fill="#FDE047" data-id="element-3088" />
            <path d="M0 -10 L2 -3 L9 -3 L4 2 L6 9 L0 4 L-6 9 L-4 2 L-9 -3 L-2 -3 Z" fill="#FEF08A" data-id="element-3089" />
          </g>;
      case 'scepter':
        return <g className="djay-accessory" transform="translate(165, 90) rotate(15) scale(1.5)" data-id="element-3090">
            <rect x="-3" y="0" width="6" height="50" fill="#FCD34D" rx="3" data-id="element-3091" />
            <circle cx="0" cy="-5" r="12" fill="#8B5CF6" data-id="element-3092" />
            <circle cx="0" cy="-5" r="8" fill="#A78BFA" data-id="element-3093" />
            <path d="M-15 -5 L15 -5 M0 -20 L0 10" stroke="#FCD34D" strokeWidth="3" data-id="element-3094" />
            <circle cx="0" cy="-5" r="3" fill="#FDE047" data-id="element-3095" />
          </g>;
      case 'cosmicOrb':
        return <g className="djay-accessory" transform="translate(165, 100) scale(1.5)" data-id="element-3096">
            <circle cx="0" cy="0" r="15" fill={`url(#${uid}-cosmic-orb-grad)`} data-id="element-3097" />
            <circle cx="0" cy="0" r="15" fill="none" stroke="#C084FC" strokeWidth="2" strokeDasharray="5 5" className="djay-spin" data-id="element-3098" />
            <circle cx="-5" cy="-5" r="3" fill="white" fillOpacity="0.8" data-id="element-3099" />
            <circle cx="6" cy="4" r="1.5" fill="white" fillOpacity="0.6" data-id="element-3100" />
            <path d="M-20 0 Q0 -10 20 0 Q0 10 -20 0 Z" fill="#E879F9" fillOpacity="0.4" transform="rotate(30)" data-id="element-3101" />
            <defs data-id="element-3102">
              <radialGradient id={`${uid}-cosmic-orb-grad`} cx="30%" cy="30%" r="70%" data-id="element-3103">
                <stop offset="0%" stopColor="#E879F9" data-id="element-3104" />
                <stop offset="50%" stopColor="#A855F7" data-id="element-3105" />
                <stop offset="100%" stopColor="#4C1D95" data-id="element-3106" />
              </radialGradient>
            </defs>
          </g>;
      case 'shadowOrb':
        return <g className="djay-accessory" transform="translate(165, 100) scale(1.5)" data-id="element-3107">
            <circle cx="0" cy="0" r="15" fill="#0F172A" data-id="element-3108" />
            <circle cx="0" cy="0" r="17" fill="none" stroke="#334155" strokeWidth="2" strokeDasharray="8 4" className="djay-spin-reverse" data-id="element-3109" />
            <circle cx="-4" cy="-4" r="4" fill="#475569" fillOpacity="0.5" data-id="element-3110" />
            <path d="M-10 5 Q0 -5 10 5 Q0 15 -10 5 Z" fill="#1E293B" data-id="element-3111" />
            <circle cx="0" cy="0" r="20" fill="none" stroke="#EF4444" strokeWidth="1" strokeOpacity="0.5" data-id="element-3112" />
          </g>;
      // HEAD ACCESSORIES
      case 'doubleFlameCrown':
        return <g className="djay-accessory" transform="translate(100, 15) scale(1.5)" data-id="element-3113">
            <path d="M-20 20 L-10 0 L0 15 L10 0 L20 20 Z" fill="#F59E0B" data-id="element-3114" />
            <path d="M-15 20 L-10 5 L0 15 L10 5 L15 20 Z" fill="#FCD34D" data-id="element-3115" />
            <circle cx="-10" cy="-2" r="3" fill="#EF4444" data-id="element-3116" />
            <circle cx="10" cy="-2" r="3" fill="#EF4444" data-id="element-3117" />
          </g>;
      case 'cap':
        return <g className="djay-accessory" transform="translate(100, 25) scale(1.4)" data-id="element-3118">
            <path d="M-25 10 C-25 -10 25 -10 25 10 Z" fill="#3B82F6" data-id="element-3119" />
            <path d="M-35 10 L30 10" stroke="#2563EB" strokeWidth="4" strokeLinecap="round" data-id="element-3120" />
            <circle cx="0" cy="-5" r="3" fill="#60A5FA" data-id="element-3121" />
          </g>;
      // FACE ACCESSORIES
      case 'sunglasses':
        return <g className="djay-accessory" transform="translate(100, 95) scale(1.4)" data-id="element-3122">
            <rect x="-35" y="-10" width="30" height="20" rx="4" fill="#0F172A" data-id="element-3123" />
            <rect x="5" y="-10" width="30" height="20" rx="4" fill="#0F172A" data-id="element-3124" />
            <path d="M-5 -5 L5 -5" stroke="#0F172A" strokeWidth="4" data-id="element-3125" />
            <path d="M-35 -5 L-45 -10" stroke="#0F172A" strokeWidth="4" strokeLinecap="round" data-id="element-3126" />
            <path d="M35 -5 L45 -10" stroke="#0F172A" strokeWidth="4" strokeLinecap="round" data-id="element-3127" />
            {/* Reflections */}
            <path d="M-30 -5 L-15 -5" stroke="#F8FAFC" strokeWidth="2" strokeLinecap="round" opacity="0.5" data-id="element-3128" />
            <path d="M10 -5 L25 -5" stroke="#F8FAFC" strokeWidth="2" strokeLinecap="round" opacity="0.5" data-id="element-3129" />
          </g>;
      // NECK ACCESSORIES
      case 'badge100':
        return <g className="djay-accessory" transform="translate(100, 140) scale(1.3)" data-id="element-3130">
            <path d="M-20 -20 L0 0 L20 -20" fill="none" stroke="#3B82F6" strokeWidth="6" data-id="element-3131" />
            <circle cx="0" cy="0" r="18" fill="#EF4444" data-id="element-3132" />
            <circle cx="0" cy="0" r="14" fill="none" stroke="#FEF08A" strokeWidth="2" strokeDasharray="4 2" data-id="element-3133" />
            <text x="0" y="5" fontFamily="sans-serif" fontSize="14" fontWeight="bold" fill="white" textAnchor="middle" data-id="element-3134">
              100
            </text>
          </g>;
      case 'medal':
        return <g className="djay-accessory" transform="translate(100, 140) scale(1.3)" data-id="element-3135">
            <path d="M-20 -30 L0 0 L20 -30" fill="none" stroke="#3B82F6" strokeWidth="6" data-id="element-3136" />
            <path d="M-15 -30 L0 0 L15 -30" fill="none" stroke="#60A5FA" strokeWidth="2" data-id="element-3137" />
            <circle cx="0" cy="5" r="16" fill="#F59E0B" data-id="element-3138" />
            <circle cx="0" cy="5" r="12" fill="#FCD34D" data-id="element-3139" />
            <text x="0" y="10" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#92400E" textAnchor="middle" data-id="element-3140">
              1
            </text>
          </g>;
      case 'leafGarland':
        return <g className="djay-accessory" transform="translate(100, 130) scale(1.3)" data-id="element-3141">
            <path d="M-45 -10 Q0 30 45 -10" fill="none" stroke="#22C55E" strokeWidth="4" data-id="element-3142" />
            <path d="M-35 10 Q-40 5 -35 0 Q-30 5 -35 10 Z" fill="#4ADE80" data-id="element-3143" />
            <path d="M-15 18 Q-20 13 -15 8 Q-10 13 -15 18 Z" fill="#4ADE80" data-id="element-3144" />
            <path d="M5 20 Q0 15 5 10 Q10 15 5 20 Z" fill="#4ADE80" data-id="element-3145" />
            <path d="M25 18 Q20 13 25 8 Q30 13 25 18 Z" fill="#4ADE80" data-id="element-3146" />
            <path d="M40 8 Q35 3 40 -2 Q45 3 40 8 Z" fill="#4ADE80" data-id="element-3147" />
          </g>;
      case 'goldChain':
        return <g className="djay-accessory" transform="translate(100, 130) scale(1.4)" data-id="element-3148">
            <path d="M-30 -10 Q0 30 30 -10" fill="none" stroke="#F59E0B" strokeWidth="6" strokeLinecap="round" data-id="element-3149" />
            <path d="M-30 -10 Q0 30 30 -10" fill="none" stroke="#FCD34D" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 2" data-id="element-3150" />
            <circle cx="0" cy="10" r="8" fill="#F59E0B" data-id="element-3151" />
            <circle cx="0" cy="10" r="5" fill="#FCD34D" data-id="element-3152" />
          </g>;
      // EFFECTS (Background/Surrounding)
      case 'tripleStars':
        return <g className="djay-accessory" transform="translate(100, 20) scale(1.5)" data-id="element-3153">
            <g transform="translate(-35, 0) scale(0.7)" data-id="element-3154">
              <path d="M0 -15 L4 -4 L15 -4 L6 3 L10 14 L0 7 L-10 14 L-6 3 L-15 -4 L-4 -4 Z" fill="#FDE047" data-id="element-3155" />
            </g>
            <g transform="translate(0, -15)" data-id="element-3156">
              <path d="M0 -15 L4 -4 L15 -4 L6 3 L10 14 L0 7 L-10 14 L-6 3 L-15 -4 L-4 -4 Z" fill="#FDE047" data-id="element-3157" />
            </g>
            <g transform="translate(35, 0) scale(0.7)" data-id="element-3158">
              <path d="M0 -15 L4 -4 L15 -4 L6 3 L10 14 L0 7 L-10 14 L-6 3 L-15 -4 L-4 -4 Z" fill="#FDE047" data-id="element-3159" />
            </g>
          </g>;
      default:
        return null;
    }
  };
  const renderTierElements = () => {
    if (!showDetails) return null;
    switch (tier) {
      case 'pro':
        return <g className="djay-tier-pro" data-id="element-3160">
            <path d="M45 75 Q100 85 155 75 L150 65 Q100 75 50 65 Z" fill="#3B82F6" data-id="element-3161" />
            <path d="M45 75 Q100 85 155 75 L150 65 Q100 75 50 65 Z" fill={`url(#${uid}-pro-band-grad)`} opacity="0.5" data-id="element-3162" />
            <path d="M145 70 L165 60 L160 75 Z" fill="#2563EB" data-id="element-3163" />
            <defs data-id="element-3164">
              <linearGradient id={`${uid}-pro-band-grad`} x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-3165">
                <stop offset="0%" stopColor="#1D4ED8" data-id="element-3166" />
                <stop offset="50%" stopColor="#60A5FA" data-id="element-3167" />
                <stop offset="100%" stopColor="#1D4ED8" data-id="element-3168" />
              </linearGradient>
            </defs>
          </g>;
      case 'master':
        return <g className="djay-tier-master" data-id="element-3169">
            <path d="M60 55 L70 25 L85 45 L100 15 L115 45 L130 25 L140 55 Z" fill="#FBBF24" stroke="#D97706" strokeWidth="2" strokeLinejoin="round" data-id="element-3170" />
            <circle cx="70" cy="25" r="4" fill="#EF4444" data-id="element-3171" />
            <circle cx="100" cy="15" r="5" fill="#3B82F6" data-id="element-3172" />
            <circle cx="130" cy="25" r="4" fill="#10B981" data-id="element-3173" />
            <path d="M65 50 Q100 60 135 50" fill="none" stroke="#D97706" strokeWidth="2" data-id="element-3174" />
            <path d="M40 120 Q20 150 30 190 L60 180 Q45 140 50 120 Z" fill="#7C3AED" data-id="element-3175" />
            <path d="M160 120 Q180 150 170 190 L140 180 Q155 140 150 120 Z" fill="#7C3AED" data-id="element-3176" />
            <path d="M45 125 Q100 145 155 125 L150 115 Q100 135 50 115 Z" fill="#6D28D9" data-id="element-3177" />
          </g>;
      default:
        return null;
    }
  };
  // Inject animation styles once (singleton)
  if (animate) ensureAnimationStyles();
  return <div className={`relative inline-flex items-center justify-center ${className}`} data-id="element-3178">
      <svg width={pixelSize} height={pixelSize} viewBox="0 0 200 220" fill="none" xmlns="http://www.w3.org/2000/svg" className={`djay-avatar ${animate ? 'djay-animated' : ''}`} style={{
      overflow: 'visible'
    }} role="img" aria-label={`Djay mascotte (${emotion})`} data-id="element-3179">
        <defs data-id="element-3180">
          {variant === 'rainbow' ? <linearGradient id={`${uid}-body-grad`} x1="35" y1="45" x2="165" y2="195" gradientUnits="userSpaceOnUse" data-id="element-3181">
              <stop offset="0%" stopColor="#F87171" data-id="element-3182" />
              <stop offset="20%" stopColor="#FB923C" data-id="element-3183" />
              <stop offset="40%" stopColor="#FBBF24" data-id="element-3184" />
              <stop offset="60%" stopColor="#34D399" data-id="element-3185" />
              <stop offset="80%" stopColor="#60A5FA" data-id="element-3186" />
              <stop offset="100%" stopColor="#A78BFA" data-id="element-3187" />
            </linearGradient> : <linearGradient id={`${uid}-body-grad`} x1="35" y1="45" x2="165" y2="195" gradientUnits="userSpaceOnUse" data-id="element-3188">
              <stop offset="0%" stopColor={colors.bodyGradient[0]} data-id="element-3189" />
              <stop offset="45%" stopColor={colors.bodyGradient[1]} data-id="element-3190" />
              <stop offset="100%" stopColor={colors.bodyGradient[2]} data-id="element-3191" />
            </linearGradient>}
        </defs>
        {!hideBody && <>
            <g className="djay-shadow" data-id="element-3192">
              <ellipse cx="100" cy="205" rx="48" ry="10" fill="#1E293B" fillOpacity="0.12" data-id="element-3193" />
            </g>
            {showTail && <g className="djay-tail" data-id="element-3194">
                <path d="M152 150 C162 142, 176 140, 182 152 C188 164, 178 178, 168 176 C158 174, 166 160, 176 158 C182 156, 180 168, 174 172" stroke={colors.tailStroke} strokeWidth="10" strokeLinecap="round" fill="none" data-id="element-3195" />
                <path d="M152 150 C162 142, 176 140, 182 152 C188 164, 178 178, 168 176 C158 174, 166 160, 176 158 C182 156, 180 168, 174 172" stroke={colors.armColor} strokeWidth="7" strokeLinecap="round" fill="none" data-id="element-3196" />
                <path d="M154 148 C162 141, 174 140, 180 150" stroke={colors.tailHighlight} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3197" />
                <circle cx="174" cy="171" r="4" fill={colors.armColor} data-id="element-3198" />
                <circle cx="174" cy="171" r="2.5" fill={colors.tailHighlight} data-id="element-3199" />
              </g>}
            {renderArms()}
            <g className="djay-body" data-id="element-3200">
              <path d="M100 195 C55 195, 35 155, 35 115 C35 65, 60 45, 100 45 C140 45, 165 65, 165 115 C165 155, 145 195, 100 195 Z" fill={`url(#${uid}-body-grad)`} data-id="element-3201" />
              <path d="M68 78 C52 95, 48 125, 53 150 C48 125, 52 88, 73 73 Z" fill={colors.tailHighlight} fillOpacity="0.5" data-id="element-3202" />
              <path d="M146 98 C156 120, 156 152, 146 178 C162 152, 162 115, 146 93 Z" fill={colors.feetDark} fillOpacity="0.3" data-id="element-3203" />
              {showDetails && <ellipse cx="100" cy="155" rx="25" ry="18" fill={colors.tailHighlight} fillOpacity="0.25" data-id="element-3204" />}
              {variant === 'cosmic' && <g className="djay-cosmic-stars" data-id="element-3205">
                  <circle cx="60" cy="100" r="1.5" fill="white" fillOpacity="0.8" data-id="element-3206" />
                  <circle cx="140" cy="140" r="1.5" fill="white" fillOpacity="0.6" data-id="element-3207" />
                  <circle cx="80" cy="160" r="2" fill="white" fillOpacity="0.7" data-id="element-3208" />
                  <circle cx="120" cy="70" r="1" fill="white" fillOpacity="0.9" data-id="element-3209" />
                  <circle cx="50" cy="130" r="1" fill="white" fillOpacity="0.5" data-id="element-3210" />
                </g>}
              {variant === 'rainbow' && <g className="djay-rainbow-sparkles" data-id="element-3211">
                  <circle cx="62" cy="100" r="2" fill="#FDE047" fillOpacity="0.85" data-id="element-3212" />
                  <circle cx="138" cy="140" r="2" fill="#F472B6" fillOpacity="0.85" data-id="element-3213" />
                  <circle cx="80" cy="165" r="2.5" fill="#A78BFA" fillOpacity="0.85" data-id="element-3214" />
                  <circle cx="125" cy="75" r="1.5" fill="#34D399" fillOpacity="0.9" data-id="element-3215" />
                  <circle cx="55" cy="135" r="1.5" fill="#60A5FA" fillOpacity="0.85" data-id="element-3216" />
                  <circle cx="145" cy="105" r="1.5" fill="#FB923C" fillOpacity="0.85" data-id="element-3217" />
                </g>}
            </g>
            <g className="djay-flame" data-id="element-3218">
              <path d="M100 52 C88 46, 83 34, 86 18 C90 8, 100 2, 100 2 C100 2, 110 8, 114 18 C117 34, 112 46, 100 52 Z" fill={colors.flameColors[0]} data-id="element-3219" />
              <path d="M100 50 C89 45, 85 34, 88 20 C91 10, 100 5, 100 5 C100 5, 109 10, 112 20 C115 34, 111 45, 100 50 Z" fill={colors.flameColors[1]} data-id="element-3220" />
              <path d="M100 48 C93 44, 90 35, 92 24 C95 15, 100 11, 100 11 C100 11, 105 15, 108 24 C110 35, 107 44, 100 48 Z" fill={colors.flameColors[2]} data-id="element-3221" />
              <path d="M97 40 C94 36, 93 29, 95 23 C96 19, 98 17, 98 17 C98 17, 96 24, 97 31 C97 35, 97 38, 97 40 Z" fill={colors.flameColors[3]} data-id="element-3222" />
            </g>
            {renderTierElements()}
            <g className="djay-glasses" data-id="element-3223">
              <path d="M90 92 C96 87, 104 87, 110 92" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-3224" />
              <rect x="49" y="82" width="42" height="36" rx="7" ry="7" fill="none" stroke={colors.glassesFrame} strokeWidth="5" data-id="element-3225" />
              <rect x="52" y="85" width="36" height="30" rx="5" ry="5" fill={colors.glassesLens} data-id="element-3226" />
              <rect x="55" y="87" width="12" height="6" rx="3" ry="3" fill="white" fillOpacity="0.55" data-id="element-3227" />
              <rect x="109" y="82" width="42" height="36" rx="7" ry="7" fill="none" stroke={colors.glassesFrame} strokeWidth="5" data-id="element-3228" />
              <rect x="112" y="85" width="36" height="30" rx="5" ry="5" fill={colors.glassesLens} data-id="element-3229" />
              <rect x="115" y="87" width="12" height="6" rx="3" ry="3" fill="white" fillOpacity="0.55" data-id="element-3230" />
              <path d="M49 96 L40 94" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" data-id="element-3231" />
              <path d="M151 96 L160 94" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" data-id="element-3232" />
            </g>
            {renderEyes()}
            {renderMouth()}
            {showDetails && <g className="djay-cheeks" data-id="element-3233">
                <ellipse cx="52" cy="120" rx="9" ry="5" fill="#FDBA74" fillOpacity="0.5" data-id="element-3234" />
                <ellipse cx="148" cy="120" rx="9" ry="5" fill="#FDBA74" fillOpacity="0.5" data-id="element-3235" />
              </g>}
            {showFullArms && <g className="djay-feet" data-id="element-3236">
                <ellipse cx="80" cy="196" rx="14" ry="6" fill={colors.feetDark} data-id="element-3237" />
                <ellipse cx="80" cy="195" rx="14" ry="6" fill={colors.feetLight} data-id="element-3238" />
                <ellipse cx="120" cy="196" rx="14" ry="6" fill={colors.feetDark} data-id="element-3239" />
                <ellipse cx="120" cy="195" rx="14" ry="6" fill={colors.feetLight} data-id="element-3240" />
              </g>}
          </>}
        <g className="djay-accessory-float" data-id="element-3241">{renderAccessory()}</g>
      </svg>
    </div>;
}